import {BaseService} from "../../../infrastructure/domain-services/base-service";
import {BaseConfig} from "../../../infrastructure/config/base-config";
import {BaseMatchModel} from "./models/base-match-model";
import {RouteModel} from "./models/route-model";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {NavigationManager} from "../../managers/navigation-manager";
import {REDIRECT_NAMES, ROUTE_NAMES, SITE_DOMAINS} from "../../consts";
import {computed, observable} from "mobx";
import {IOC} from "../../../infrastructure/ioc";
import {AppModuleState} from "../../app/models/app-module.state";
import {BaseRouteConfig} from "./models/base-route-config";
import {BaseRouteMetaDataModel} from "../../../infrastructure/models/route/base-route.meta-data.model";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {YesterdayRedirectModel} from "./models/redirects/yesterday.redirect-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {TomorrowRedirectModel} from "./models/redirects/tomorrow.redirect-model";
import type {RedirectModel} from "./models/redirects/redirect-model";
import {TodayRedirectModel} from "./models/redirects/today.redirect-model";
import {FootballRedirectModel} from "./models/redirects/football.redirect-model";

@singleton
export class RouterController extends BaseService {
  routes: Array<RouteModel> = [];
  redirects: Array<RedirectModel> = [];
  routeChangeCallbacks: Array<Function> = [];
  @observable currentMatch: BaseMatchModel;
  navigationManager: NavigationManager;
  initialLinkModel: RouteModel;

  static injectables = [NavigationManager, IOC, DatetimeUtils]

  constructor(navigationManager: NavigationManager, ioc: IOC, datetimeUtils: DatetimeUtils) {
    super();

    this.navigationManager = navigationManager;
    this.datetimeUtils = datetimeUtils;
    this.ioc = ioc;
  }

  handleMatchChange(match: BaseMatchModel) {
    this.currentMatch = match;
    this.navigationManager.currentLinkModel = this.linkModel;
  }

  get isCurrentRouteMatchValid(): Boolean {
    return this.currentRoute?.config?.isValidMatch(this.currentMatch, this.currentRoute);
  }

  @computed get currentRoute(): RouteModel {
    if (!this.currentMatch) {
      return null;
    }

    const currentRoute: RouteModel = this.findRoute(this.currentMatch);

    return currentRoute;
  }

  get linkModel(): BaseLinkModel {
    if (!this.currentMatch || !this.currentRoute) {
      return null;
    }

    const linkModel: BaseLinkModel = new BaseLinkModel(this.currentRoute.name, this.currentMatch.params, this.currentMatch.isExact, this.currentRoute.cultureName, this.currentMatch.screen, this.currentMatch.hashParams, this.currentMatch.isLinkTag,  this.currentRoute.config?.routeType, this.currentRoute.config?.parent);

    return linkModel;
  }

  getRoutes() {
    const pagesRoutes = this.routes?.map(route => route?.pages).flat();
    const routes = [this.routes, pagesRoutes].flat();

    return routes;
  }

  getCurrentRoute(matchModel: BaseMatchModel): RouteModel {
    const currentRoute: RouteModel = this.findRoute(matchModel);

    return currentRoute;
  }

  onRouteExit(route: RouteModel) {
    this.navigationManager.clearLinkModel();
  }

  initializeRoutes(configurations: Array<BaseConfig> = [], cultureName: String, initialLinkModel: RouteModel, pages) {
    this.initialLinkModel = initialLinkModel;

    this.routes = configurations.map((routeConfig: BaseRouteConfig) => {
      const pagesRoutes = pages?.get(routeConfig.name)?.map(pageConfig => this.createRouteModel(pageConfig, cultureName))

      return this.createRouteModel(routeConfig, cultureName, pagesRoutes)
    }).filter(x => x);

    this.redirects = this.createRedirects(cultureName)
  }

  createRouteModel(routeConfig, cultureName, pages = []) {
    const config = routeConfig;

    if (!config) {
      return null;
    }

    const metadataType: BaseRouteMetaDataModel = routeConfig.metadataType && this.ioc.getInstance(routeConfig.metadataType);

    if (metadataType != null) {
      metadataType.ioc = this.ioc;
    }

    return routeConfig && new RouteModel(routeConfig, cultureName, metadataType, pages);
  }

  createRedirects(cultureName) {
    const redirects = [];

    const footballRedirectModel: FootballRedirectModel = new FootballRedirectModel(REDIRECT_NAMES.football, cultureName);
    const yesterdayRedirect: YesterdayRedirectModel = new YesterdayRedirectModel(REDIRECT_NAMES.yesterday, cultureName, this.datetimeUtils);
    const todayRedirectModel: TodayRedirectModel = new TodayRedirectModel(REDIRECT_NAMES.today, cultureName, this.datetimeUtils);
    const tomorrowRedirect: TomorrowRedirectModel = new TomorrowRedirectModel(REDIRECT_NAMES.tomorrow, cultureName, this.datetimeUtils);

    redirects.push(footballRedirectModel);
    redirects.push(yesterdayRedirect);
    redirects.push(todayRedirectModel);
    redirects.push(tomorrowRedirect);

    return redirects
  }

  getRedirects() {
    return this.redirects;
  }

  findRoute(match: BaseMatchModel): RouteModel {
    const pagesRoutes = this.routes?.map(route => route?.pages).flat();
    const routes = [pagesRoutes, this.routes].flat();

    const requestedRoute: RouteModel = routes.find((route: RouteModel) => {
      return route.config.isValidMatch(match, route) && ((route.config.paths?.includes(match.path) || route.config.path === match.path));
    });

    return requestedRoute;
  }

  findRouteByName(routeName: ROUTE_NAMES): RouteModel {
    const pagesRoutes = this.routes?.map(route => route?.pages).flat();
    const routes = [this.routes, pagesRoutes].flat();

    const requestedRoute: RouteModel = routes.find((route: RouteModel) => route.name === routeName);

    return requestedRoute;
  }

  registerToRouteChange(id, callback) {
    this.routeChangeCallbacks.push({id, callback});
  }

  unregisterToRouteChange(id) {
    this.routeChangeCallbacks = this.routeChangeCallbacks.filter(c => c.id !== id);
  }

  onRouteChange(route: RouteModel) {
    this.routeChangeCallbacks.forEach((c) => c.callback(route));
  }

  isFormalRoute(route: RouteModel): Boolean {
    const appState: AppModuleState = this.ioc.getInstance(AppModuleState);
    const isWidget: Boolean = appState.settings.isWidget;
    const isInitialRoute: Boolean = this.initialLinkModel && route && route.name === this.initialLinkModel.name;
    const isFormalRoute: Boolean = !isWidget || !isInitialRoute;

    return isFormalRoute;
  }

  preloadRouteComponent(routeName: ROUTE_NAMES): * {
    const route: RouteModel = this.findRouteByName(routeName);
    if (route && route.config && route.config.component && route.config.component.preload) {
      route.config.component.preload();
    }
  }

  currentUrl(useHash: String = false): String {
    const url: String = this.currentRoute ? `${SITE_DOMAINS.full}${this.navigationManager.createUrl(this.currentRoute.path, this.linkModel, useHash)}` : null;

    return url;
  }
}
